import {CURRENCY} from '@invoice/core/constants/currency';

import {ALPHA_2_COUNTRY} from '@invoice/core/constants/env';

import {$invoiceTypeCodeStatusInfo} from '../../models/common';
import {PackageTypes, TRANSPORT_PACKAGE_TYPES} from '../../types/gtins';
import {
    InfoValues,
    InvoiceLine,
    InvoiceTypeCodeStatus,
    InvoiceTypeCodeStatusInfo,
    PageValues,
    TaxesAndTotal,
} from '../../types/invoices';

import {ifCategoryOfO, splitValue} from './consts';
import {setDocumentBase} from './setDocumentBase';

function mappingCalculationsByTaxCategory(taxes: TaxesAndTotal) {
    return taxes.calculationsByTaxCategory.map((i) => {
        const {
            taxableAmountDisplay,
            taxAmountDisplay,
            taxCategory,
            taxCategoryDisplay,
            rate,
            taxExemptionReason,
        } = i;
        const requiedFields = [taxableAmountDisplay, taxAmountDisplay, taxCategoryDisplay];
        const allFieldsValid = requiedFields.every((i) => !!i);

        if (!allFieldsValid) {
            return;
        }

        return {
            'cbc:TaxableAmount': {
                '#text': taxableAmountDisplay,
                '@_currencyID': CURRENCY,
            },
            'cbc:TaxAmount': {
                '#text': taxAmountDisplay,
                '@_currencyID': CURRENCY,
            },
            'cac:TaxCategory': {
                'cbc:ID': splitValue(taxCategory as string),
                ...(rate && {'cbc:Percent': Number(rate)}),
                ...(taxExemptionReason && {'cbc:TaxExemptionReason': taxExemptionReason}),
                'cac:TaxScheme': {
                    'cbc:ID': 'VAT',
                },
            },
        };
    });
}

const isTransportPackageType = (packagingType?: PackageTypes) =>
    packagingType && TRANSPORT_PACKAGE_TYPES.includes(packagingType);

function mappingInvoiceLine(invoiceLine: InvoiceLine[], typeCodeStatus: InvoiceTypeCodeStatusInfo) {
    return invoiceLine.map((i, index) => ({
        'cbc:ID': index,
        [typeCodeStatus.label === InvoiceTypeCodeStatus.ANNULLING ||
        typeCodeStatus.label === InvoiceTypeCodeStatus.CORRECTIVE_ANNULLING ||
        typeCodeStatus.label === InvoiceTypeCodeStatus.DIRECT_ANNULLING
            ? 'cbc:CreditedQuantity'
            : 'cbc:InvoicedQuantity']: {
            '#text': i.qty,
            '@_unitCode': 'C62',
        },
        'cbc:LineExtensionAmount': {
            '#text': i.lines,
            '@_currencyID': CURRENCY,
        },
        ...(i.charges &&
            i.charges.isCharge &&
            i.charges.amountXaf && {
                'cac:AllowanceCharge': [
                    {
                        'cbc:ChargeIndicator': i.charges.isCharge,
                        ...(i.charges.reason && {'cbc:AllowanceChargeReason': i.charges.reason}),
                        ...(i.charges.ratePercent && {
                            'cbc:MultiplierFactorNumeric': i.charges.ratePercent,
                        }),
                        'cbc:Amount': {
                            '#text': i.charges.amountXaf,
                            '@_currencyID': CURRENCY,
                        },
                        ...(i.charges.baseAmount && {
                            'cbc:BaseAmount': {
                                '#text': i.charges.baseAmount,
                                '@_currencyID': CURRENCY,
                            },
                        }),
                    },
                ],
            }),
        'cac:Item': {
            'cbc:Description': i.description,
            'cbc:Name': i.description,
            ...(!!i.itemCode && {
                'cac:SellersItemIdentification': {
                    'cbc:ID': {
                        '#text': i.itemCode,
                    },
                },
            }),
            ...(!!i.gtin &&
                !isTransportPackageType(i.packagingType) && {
                    'cac:StandardItemIdentification': {
                        'cbc:ID': {
                            '#text': i.gtin,
                            '@_schemeID': '0160',
                        },
                    },
                }),
            'cac:CommodityClassification': [
                ...(i.serviceCategory && i.serviceCategory.value
                    ? [
                          {
                              'cbc:ItemClassificationCode': {
                                  '#text': i.serviceCategory.value,
                                  '@_listID': i.isHsn,
                              },
                          },
                      ]
                    : []),
            ],
            'cac:ClassifiedTaxCategory': {
                'cbc:ID': i.vatCategory.value,
                ...(!ifCategoryOfO(i.vatCategory.value) && {'cbc:Percent': i.vat}),
                'cac:TaxScheme': {
                    'cbc:ID': 'VAT',
                },
            },
            'cac:AdditionalItemProperty': [
                ...(i.code && i.packagingType
                    ? [
                          {
                              'cbc:Name': TRANSPORT_PACKAGE_TYPES.includes(i.packagingType)
                                  ? 'transportCode'
                                  : 'markingCode',
                              'cbc:Value': i.code,
                          },
                      ]
                    : []),
                {
                    'cbc:Name': `Line Amount with VAT (${CURRENCY})`,
                    'cbc:Value': i.lineAmountWVat,
                },
                {
                    'cbc:Name': 'level',
                    'cbc:Value': i.level ?? 1,
                },
                ...(i.isCombined
                    ? [
                          {
                              'cbc:Name': 'isCombined',
                              'cbc:Value': true,
                          },
                      ]
                    : []),
                ...(i.packagingType
                    ? [
                          {
                              'cbc:Name': 'packagingType',
                              'cbc:Value': i.packagingType,
                          },
                      ]
                    : []),
                ...(i.parent
                    ? [
                          {
                              'cbc:Name': 'parent',
                              'cbc:Value': i.parent,
                          },
                      ]
                    : []),
                ...(i.combinedCodeId?.toString()
                    ? [
                          {
                              'cbc:Name': 'combinedCodeId',
                              'cbc:Value': i.combinedCodeId,
                          },
                      ]
                    : []),
                ...(i.serviceCategory && i.serviceCategory.label
                    ? [
                          {
                              'cbc:Name': 'productCodeDescription',
                              'cbc:Value': i.serviceCategory.label,
                          },
                      ]
                    : []),
            ],
        },
        'cac:Price': {
            'cbc:PriceAmount': {
                '#text': i.price,
                '@_currencyID': CURRENCY,
            },
        },
    }));
}

function mappingAllowanceCharge(taxes: NonNullable<TaxesAndTotal['allowanceCharge']>) {
    return taxes.map((i) => ({
        'cbc:ChargeIndicator': i.isCharge === 'charge',
        'cbc:AllowanceChargeReason': i.reason,
        ...(i.rate && {
            'cbc:MultiplierFactorNumeric': i.rate,
        }),
        'cbc:Amount': {
            '#text': Number(i.totalAmount).toFixed(2),
            '@_currencyID': CURRENCY,
        },
        ...(i.baseAmount && {
            'cbc:BaseAmount': {
                '#text': i.baseAmount,
                '@_currencyID': CURRENCY,
            },
        }),
        'cac:TaxCategory': {
            'cbc:ID': i.vatCategory?.value,
            'cbc:Percent': i.vat,
            'cac:TaxScheme': {
                'cbc:ID': 'VAT',
            },
        },
    }));
}

export const setTestingXml = (
    infoValues: InfoValues,
    pageValues: PageValues,
    taxesAndTotal: TaxesAndTotal,
    invoiceLine: InvoiceLine[],
) => {
    const typeCodeStatus = $invoiceTypeCodeStatusInfo.getState();
    const invoiceName =
        typeCodeStatus?.label === InvoiceTypeCodeStatus.ANNULLING ||
        typeCodeStatus?.label === InvoiceTypeCodeStatus.CORRECTIVE_ANNULLING ||
        typeCodeStatus?.label === InvoiceTypeCodeStatus.DIRECT_ANNULLING
            ? 'CreditNote'
            : 'Invoice';
    return {
        [invoiceName]: {
            ...setDocumentBase(typeCodeStatus, infoValues, taxesAndTotal, pageValues, CURRENCY),
            'cac:ContractDocumentReference': {
                'cbc:ID': pageValues.contractDocumentReference,
            },
            'cac:AccountingSupplierParty': {
                'cac:Party': {
                    'cbc:EndpointID': {
                        '#text': infoValues.companyTIN,
                        '@_schemeID': '0203',
                    },
                    'cac:PartyName': {
                        'cbc:Name': infoValues.companyNameAndTIN,
                    },
                    'cac:PostalAddress': {
                        'cbc:StreetName': infoValues.supplierStreetName ?? 'NOT_AVAILABLE',
                        'cbc:CityName': infoValues.supplierCityName ?? 'NOT_AVAILABLE',
                        'cbc:PostalZone': infoValues.supplierPostalZone ?? 'NOT_AVAILABLE',
                        'cac:Country': {
                            'cbc:IdentificationCode': infoValues.supplierCountry ?? ALPHA_2_COUNTRY,
                        },
                    },
                    ...(!invoiceLine.find((i) => ifCategoryOfO(i.vatCategory?.value)) && {
                        'cac:PartyTaxScheme': {
                            'cbc:CompanyID': `${ALPHA_2_COUNTRY}${infoValues.companyTIN}`,
                            'cac:TaxScheme': {
                                'cbc:ID': 'VAT',
                            },
                        },
                    }),
                    'cac:PartyLegalEntity': {
                        'cbc:RegistrationName': infoValues.companyNameAndTIN,
                        'cbc:CompanyID': infoValues.companyTIN,
                    },
                    'cac:Contact': {
                        'cbc:Telephone': infoValues.supplierPhoneNumber,
                        'cbc:ElectronicMail': infoValues.supplierEmail,
                    },
                },
            },
            'cac:AccountingCustomerParty': {
                'cac:Party': {
                    'cbc:EndpointID': {
                        '#text': infoValues.customerCompanyNameOrTIN?.value ?? '',
                        '@_schemeID': '0203',
                    },
                    'cac:PostalAddress': {
                        'cbc:StreetName': infoValues.customerStreet
                            ? infoValues.customerStreet
                            : 'NOT_AVAILABLE',
                        'cbc:CityName': infoValues.customerCity
                            ? infoValues.customerCity
                            : 'NOT_AVAILABLE',
                        'cbc:PostalZone': infoValues.customerZip
                            ? infoValues.customerZip
                            : 'NOT_AVAILABLE',
                        'cac:Country': {
                            'cbc:IdentificationCode': infoValues.customerCountry
                                ? infoValues.customerCountry
                                : ALPHA_2_COUNTRY,
                        },
                    },
                    'cac:PartyLegalEntity': {
                        'cbc:RegistrationName': infoValues.customerCompanyNameOrTIN?.label ?? '',
                        'cbc:CompanyID': {
                            '#text': infoValues.customerCompanyNameOrTIN?.value ?? '',
                            '@_schemeID': '0203',
                        },
                    },
                    'cac:Contact': {
                        'cbc:Telephone': infoValues.customerPhoneNumber,
                        'cbc:ElectronicMail': infoValues.customerEmail,
                    },
                },
            },
            'cac:Delivery': {
                'cac:DeliveryLocation': {
                    'cbc:ID': {
                        '#text': infoValues.customerCompanyNameOrTIN?.value ?? '',
                        '@_schemeID': '0203',
                    },
                    'cac:Address': {
                        // ...(typeof infoValues.storageLocationName === 'object' && {
                        //     'cac:AddressLine': infoValues.storageLocationName.label,
                        // }),
                        'cac:Country': {
                            'cbc:IdentificationCode': ALPHA_2_COUNTRY,
                        },
                    },
                },
            },

            ...(infoValues.iban && {
                'cac:PaymentMeans': {
                    'cbc:PaymentMeansCode': {
                        '#text': 31,
                        '@_name': 'Credit transfer',
                    },
                    'cac:PayeeFinancialAccount': {
                        'cbc:ID': infoValues.iban,
                        ...(infoValues.bic && {
                            'cac:FinancialInstitutionBranch': {
                                'cbc:ID': infoValues.bic,
                            },
                        }),
                    },
                },
            }),

            ...(taxesAndTotal.allowanceCharge && {
                'cac:AllowanceCharge': mappingAllowanceCharge(taxesAndTotal.allowanceCharge),
            }),
            'cac:TaxTotal': {
                'cbc:TaxAmount': {
                    '#text': taxesAndTotal.taxTotal,
                    '@_currencyID': CURRENCY,
                },
                'cac:TaxSubtotal': mappingCalculationsByTaxCategory(taxesAndTotal),
            },
            'cac:LegalMonetaryTotal': {
                'cbc:LineExtensionAmount': {
                    '#text': Number(taxesAndTotal.lineExtensionAmount).toFixed(2),
                    '@_currencyID': CURRENCY,
                },
                'cbc:TaxExclusiveAmount': {
                    '#text': Number(taxesAndTotal.taxExclusiveAmountDisplay).toFixed(2),
                    '@_currencyID': CURRENCY,
                },
                'cbc:TaxInclusiveAmount': {
                    '#text': Number(taxesAndTotal.taxInclusiveAmountDisplay).toFixed(2),
                    '@_currencyID': CURRENCY,
                },
                'cbc:AllowanceTotalAmount': {
                    '#text': Number(taxesAndTotal.allowanceTotalAmountDisplay).toFixed(2),
                    '@_currencyID': CURRENCY,
                },
                'cbc:ChargeTotalAmount': {
                    '#text': Number(taxesAndTotal.chargeTotalAmountDisplay).toFixed(2),
                    '@_currencyID': CURRENCY,
                },
                'cbc:PrepaidAmount': {
                    '#text': Number(taxesAndTotal.prepaidAmount).toFixed(2),
                    '@_currencyID': CURRENCY,
                },
                'cbc:PayableRoundingAmount': {
                    '#text': taxesAndTotal.payableRoundingAmount,
                    '@_currencyID': CURRENCY,
                },
                'cbc:PayableAmount': {
                    '#text': Number(taxesAndTotal.payableAmountDisplay).toFixed(2),
                    '@_currencyID': CURRENCY,
                },
            },
            [typeCodeStatus.label === InvoiceTypeCodeStatus.ANNULLING ||
            typeCodeStatus.label === InvoiceTypeCodeStatus.CORRECTIVE_ANNULLING ||
            typeCodeStatus.label === InvoiceTypeCodeStatus.DIRECT_ANNULLING
                ? 'cac:CreditNoteLine'
                : 'cac:InvoiceLine']: mappingInvoiceLine(invoiceLine, typeCodeStatus),
        },
    };
};
